import { useEffect, useRef, useCallback, type RefObject } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IamuWordmark } from "@/components/brand/logo";
import { ViewOnlyChip } from "@/lib/project-access";
import { WorkspaceSwitcher } from "./workspace-switcher";
import { ProjectSwitcher } from "./project-switcher";
import { NavContent, SidebarFooter } from "./sidebar";

export function MobileSidebar({
  open,
  onClose,
  triggerRef,
}: {
  open: boolean;
  onClose: () => void;
  triggerRef?: RefObject<HTMLButtonElement | null>;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  const close = useCallback(() => {
    onClose();
    requestAnimationFrame(() => triggerRef?.current?.focus());
  }, [onClose, triggerRef]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "Tab" && sidebarRef.current) {
        const focusable = sidebarRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 md:hidden"
        onClick={close}
        aria-hidden="true"
      />
      <aside
        ref={sidebarRef}
        className="fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col overflow-hidden bg-sidebar shadow-lg md:hidden"
        role="dialog"
        aria-label="Navigation menu"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-sidebar-border px-3.5 pb-3 pt-4">
            <IamuWordmark className="text-sidebar-foreground" />
            <Button
              ref={closeRef}
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
              onClick={close}
              aria-label="Close navigation menu"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <div className="shrink-0 px-3.5 pt-3 space-y-1.5">
            <WorkspaceSwitcher onNavigate={close} />
            <ProjectSwitcher onNavigate={close} />
            <ViewOnlyChip className="mt-2 bg-sidebar-accent text-sidebar-foreground" />
          </div>
          <NavContent onNavigate={close} />
          <SidebarFooter onNavigate={close} />
        </div>
      </aside>
    </>
  );
}
